import { closestCorners, DndContext, DragOverlay } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import {
  Archive,
  Check,
  Edit3,
  FolderClosed,
  FolderOpen,
  MoreVertical,
  Plus,
  Search,
} from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { QueryErrorDisplay } from "@/lib/components/errors";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useServicesContext } from "../../context/services-context";
import { useDragAndDrop } from "../../hooks/use-drag-and-drop";
import { useDragSensors } from "../../hooks/use-drag-sensors";
import { useSearchExpand } from "../../hooks/use-search-expand";
import { useServicesDialogs } from "../../hooks/use-services-dialogs";
import { useArchiveService, useRestoreService } from "../../hooks/use-services-mutation";
import { useServicesUIStore } from "../../store/use-services-ui-store";
import { ArchiveManagerSheet, CategoryDialog, CreateServiceDialog } from "../dialogs";
import { ServicesEmptyState } from "../shared/services-empty-state";
import { ServicesSearch } from "../shared/services-search";
import { ServicesTable } from "../tables";

export function ServicesPageMobile() {
  const navigate = useNavigate();
  const { t } = useTranslation("services");
  const {
    currentSalon,
    filteredCategories,
    filteredServicesByCategory,
    archivedCategories: archivedCategoriesData,
    archivedServicesByCategory,
    allArchivedServices,
    totalCategories,
    hasArchivedItems,
    totalArchivedCount,
    loading,
    error,
    retry,
    isUpdatingSortOrder,
    isUpdatingServiceSortOrder,
  } = useServicesContext();
  const {
    expandedCategories,
    searchTerm,
    isDragInProgress,
    serviceDragInProgress,
    isSearchExpanded,
    isEditMode,
    setIsEditMode,
    setIsSearchExpanded,
    expandAllCategories,
    collapseAllCategories,
    setIsDragInProgress,
    setActiveDragId,
    setServiceDragInProgress,
    setActiveServiceDragId,
  } = useServicesUIStore();

  const {
    handleCreateCategory,
    handleEditCategory,
    handleCreateService,
    handleOpenArchiveManager,
    categoryDialogOpen,
    serviceDialogOpen,
    archiveDialogOpen,
    editingCategory,
    selectedCategoryForService,
    setCategoryDialogOpen,
    setServiceDialogOpen,
    setArchiveDialogOpen,
  } = useServicesDialogs();

  // Create mobile navigation elements inline to avoid hook order issues
  const isAllExpanded = expandedCategories.size === filteredCategories.length;

  const leadElement = (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant={isEditMode ? "default" : "ghost"}
        onClick={() => setIsEditMode(!isEditMode)}
        className="h-10 w-10 p-0"
      >
        {isEditMode ? <Check className="w-5 h-5" /> : <Edit3 className="w-5 h-5" />}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setIsSearchExpanded(!isSearchExpanded)}
        className="h-10 w-10 p-0"
      >
        <Search className="w-5 h-5" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className="h-10 w-10 p-0">
            <MoreVertical className="w-5 h-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            onClick={() => {
              if (isAllExpanded) {
                collapseAllCategories();
              } else {
                expandAllCategories(filteredCategories.map((c) => c.id));
              }
            }}
          >
            {isAllExpanded ? (
              <>
                <FolderClosed className="w-4 h-4 mr-2" />
                {t("actions.collapseAll")}
              </>
            ) : (
              <>
                <FolderOpen className="w-4 h-4 mr-2" />
                {t("actions.expandAll")}
              </>
            )}
          </DropdownMenuItem>
          {hasArchivedItems && (
            <DropdownMenuItem onClick={handleOpenArchiveManager}>
              <Archive className="w-4 h-4 mr-2" />
              {t("actions.archiveWithCount", { count: totalArchivedCount })}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const trailingElements = [
    <DropdownMenu key="create-menu">
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="ghost" className="h-10 w-10 p-0">
          <Plus className="w-5 h-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleCreateService()}>
          <Plus className="w-4 h-4 mr-2" />
          New Service
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCreateCategory}>
          <FolderOpen className="w-4 h-4 mr-2" />
          New Category
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>,
    <DropdownMenu key="overflow-menu">
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="ghost" className="h-10 w-10 p-0">
          <MoreVertical className="w-5 h-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => {
            if (isAllExpanded) {
              collapseAllCategories();
            } else {
              expandAllCategories(filteredCategories.map((c) => c.id));
            }
          }}
        >
          {isAllExpanded ? (
            <>
              <FolderClosed className="w-4 h-4 mr-2" />
              Collapse All
            </>
          ) : (
            <>
              <FolderOpen className="w-4 h-4 mr-2" />
              Expand All
            </>
          )}
        </DropdownMenuItem>
        {hasArchivedItems && (
          <DropdownMenuItem onClick={handleOpenArchiveManager}>
            <Archive className="w-4 h-4 mr-2" />
            Archive ({totalArchivedCount})
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>,
  ];

  const sensors = useDragSensors({ isMobile: true });

  const { handleDragStart, handleDragEnd, activeDragCategory } = useDragAndDrop();

  // Service archive/restore handlers
  const { execute: archiveService } = useArchiveService();
  const { execute: restoreService } = useRestoreService();

  const handleArchiveService = async (service: any) => {
    if (!currentSalon?.id) return;
    await archiveService(currentSalon.id, service.id);
  };

  const handleRestoreService = async (service: any) => {
    if (!currentSalon?.id) return;
    await restoreService(currentSalon.id, service.id);
  };

  // Handle auto-expand logic for search
  useSearchExpand();

  // Cleanup drag state on unmount
  useEffect(() => {
    return () => {
      setIsDragInProgress(false);
      setActiveDragId(null);
      setServiceDragInProgress(false);
      setActiveServiceDragId(null);
    };
  }, [setIsDragInProgress, setActiveDragId, setServiceDragInProgress, setActiveServiceDragId]);

  // Mobile search bottom element when expanded
  const mobileSearchElement = isSearchExpanded ? (
    <ServicesSearch variant="mobile" autoFocus />
  ) : undefined;

  // Determine content to show
  const isLoading = loading && filteredCategories.length === 0;
  const hasError = error && filteredCategories.length === 0;
  const isEmpty = filteredCategories.length === 0;

  return (
    <>
      <BaseNavigationBar
        title="Services"
        backButton={<BackButton onBack={() => navigate(-1)} />}
        leadElement={leadElement}
        trailingElements={trailingElements}
        bottomElement={mobileSearchElement}
      />

      <PageContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <span className="text-muted-foreground">Loading services...</span>
          </div>
        ) : hasError ? (
          <div className="flex items-center justify-center py-12">
            <QueryErrorDisplay error={error} onRetry={retry} />
          </div>
        ) : isEmpty ? (
          <ServicesEmptyState totalCategories={totalCategories} />
        ) : (
          <div className="space-y-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredCategories.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <ServicesTable
                  categories={filteredCategories}
                  servicesByCategory={filteredServicesByCategory}
                  salonId={currentSalon?.id || ""}
                  expandedCategories={expandedCategories}
                  onToggleCategory={(categoryId) =>
                    useServicesUIStore.getState().toggleCategory(categoryId)
                  }
                  onEditCategory={handleEditCategory}
                  onCreateService={handleCreateService}
                  onDeleteService={() => {}} // Legacy - kept for compatibility
                  onArchiveService={handleArchiveService}
                  onRestoreService={handleRestoreService}
                  isDragInProgress={isDragInProgress}
                  isUpdating={isUpdatingSortOrder}
                  isSearching={!!searchTerm.trim()}
                  isEditMode={isEditMode}
                  serviceDragInProgress={serviceDragInProgress}
                  isUpdatingServiceSortOrder={isUpdatingServiceSortOrder}
                />
              </SortableContext>

              <DragOverlay dropAnimation={null}>
                {activeDragCategory ? (
                  <div className="bg-background border border-primary/30 rounded-lg shadow-lg p-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full border"
                        style={{ backgroundColor: activeDragCategory.color }}
                      />
                      <span className="font-semibold text-lg">{activeDragCategory.name}</span>
                    </div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        )}
      </PageContent>

      {/* Dialogs */}
      <CategoryDialog
        open={categoryDialogOpen}
        onOpenChange={setCategoryDialogOpen}
        category={editingCategory}
        salonId={currentSalon?.id || ""}
        onSuccess={() => {}} // Handled automatically by hooks
      />

      <CreateServiceDialog
        open={serviceDialogOpen}
        onOpenChange={setServiceDialogOpen}
        salonId={currentSalon?.id || ""}
        defaultCategoryId={selectedCategoryForService ?? undefined}
        onSuccess={() => {}} // Handled automatically by hooks
      />

      <ArchiveManagerSheet
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        salonId={currentSalon?.id || ""}
        archivedCategories={archivedCategoriesData}
        archivedServicesByCategory={archivedServicesByCategory}
        allArchivedServices={allArchivedServices}
      />
    </>
  );
}
